import { useCallback, useEffect, useRef } from 'react'

type UseAutoReloadOptions = {
  enabled?: boolean
  intervalMs: number
  onReload: () => void | Promise<void>
}

export function useAutoReload({ enabled = true, intervalMs, onReload }: UseAutoReloadOptions): void {
  const onReloadRef = useRef(onReload)
  const isReloadingRef = useRef(false)

  useEffect(() => {
    onReloadRef.current = onReload
  }, [onReload])

  const runReload = useCallback(async (): Promise<void> => {
    if (isReloadingRef.current || document.visibilityState === 'hidden') return

    isReloadingRef.current = true
    try {
      await onReloadRef.current()
    } finally {
      isReloadingRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!enabled || intervalMs <= 0) return

    const intervalId = window.setInterval(() => {
      void runReload()
    }, intervalMs)

    const handleVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') {
        void runReload()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.clearInterval(intervalId)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [enabled, intervalMs, runReload])
}
